import { useEffect, useState } from "react";
import { Loader2, ShieldCheck, Trash2, UserPlus } from "lucide-react";
import { AdminPage } from "@/components/AdminPage";
import { useToast } from "@/components/Toast";
import { ApiClientError } from "@podecontratar/shared/contract/client-error";
import { api } from "@/lib/api";
import type { AdminRole } from "@podecontratar/shared/lib/admin-permissions";
import type { ApiContract } from "@podecontratar/shared/contract/index";
import type { EndpointOutput } from "@podecontratar/shared/contract/endpoint";

type Admin = EndpointOutput<ApiContract["GET /admin/admins"]>[number];

/** Roles an operator can actually assign — superadmin is derived, not granted. */
const ASSIGNABLE: ReadonlyArray<{ value: "admin" | "manager"; label: string; hint: string }> = [
  { value: "admin", label: "Admin", hint: "Tudo, menos técnico, eventos, backups, auditoria e admins" },
  { value: "manager", label: "Gestor(a)", hint: "Visão geral, pagamentos e cupons" },
];

const ROLE_LABEL: Record<AdminRole, string> = {
  superadmin: "Superadmin",
  admin: "Admin",
  manager: "Gestor(a)",
};

export default function AdminAdminsPage() {
  const [admins, setAdmins] = useState<Admin[] | null>(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"admin" | "manager">("admin");
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  async function refresh() {
    try {
      const { items } = await api.request("GET /admin/admins");
      setAdmins(items);
      setError(null);
    } catch {
      setError("Não foi possível carregar os admins.");
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!value || adding) return;
    setAdding(true);
    try {
      await api.request("POST /admin/admins", { body: { email: value, role } });
      toast.push(`${value} agora é ${ROLE_LABEL[role].toLowerCase()}.`, "success");
      setEmail("");
      await refresh();
    } catch (err) {
      toast.push(
        err instanceof ApiClientError && err.code === "not_found"
          ? "Nenhum usuário com esse e-mail. A pessoa precisa ter conta primeiro."
          : "Não foi possível conceder acesso.",
        "error"
      );
    } finally {
      setAdding(false);
    }
  }

  async function revoke(admin: Admin) {
    if (admin.is_super_admin) return;
    if (!window.confirm(`Remover acesso de admin de ${admin.email}?`)) return;
    try {
      await api.request("DELETE /admin/admins/:id", { params: { id: admin.id } });
      toast.push(`Acesso removido de ${admin.email}.`, "success");
      await refresh();
    } catch {
      toast.push("Não foi possível remover o acesso.", "error");
    }
  }

  async function changeRole(admin: Admin, next: "admin" | "manager") {
    if (admin.is_super_admin || admin.role === next) return;
    try {
      await api.request("PATCH /admin/admins/:id", { params: { id: admin.id }, body: { role: next } });
      toast.push(`${admin.email} agora é ${ROLE_LABEL[next].toLowerCase()}.`, "success");
      await refresh();
    } catch {
      toast.push("Não foi possível alterar o perfil.", "error");
      // The <select> is controlled but the browser already moved it to the
      // attempted option. Without a resync it keeps showing a role the
      // server rejected, which reads as a successful change.
      await refresh();
    }
  }

  return (
    <AdminPage
      title="Admins"
      description="Quem tem acesso a este painel administrativo. Conceda a usuários já cadastrados; o superadmin não pode ser alterado nem removido."
    >
      <form onSubmit={add} className="mb-6 flex flex-wrap items-center gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="e-mail do usuário"
          className="min-w-64 flex-1 rounded-full border border-border bg-surface px-4 py-2.5 text-sm text-text outline-none placeholder:text-text-tertiary focus:border-accent focus:ring-2 focus:ring-accent/20"
        />
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as "admin" | "manager")}
          aria-label="Perfil a conceder"
          className="rounded-full border border-border bg-surface px-4 py-2.5 text-sm text-text outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
        >
          {ASSIGNABLE.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
        <button
          type="submit"
          disabled={adding || !email.trim()}
          className="inline-flex items-center gap-1.5 rounded-full bg-accent px-4 py-2.5 text-sm font-medium text-text-inverse transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-60"
        >
          {adding ? (
            <Loader2 size={14} className="animate-spin" aria-hidden />
          ) : (
            <UserPlus size={14} strokeWidth={2} aria-hidden />
          )}
          Conceder acesso
        </button>
      </form>

      <p className="-mt-4 mb-6 text-xs text-text-tertiary">
        {ASSIGNABLE.map((r) => `${r.label}: ${r.hint}`).join(" · ")}
      </p>

      {error ? (
        <div className="rounded-3xl border border-danger/30 bg-danger/10 p-5 text-sm text-text">
          {error}
        </div>
      ) : admins === null ? (
        <div className="grid place-items-center rounded-3xl border border-border bg-surface py-16">
          <Loader2 size={20} className="animate-spin text-text-tertiary" aria-hidden />
        </div>
      ) : (
        <ul className="divide-y divide-border overflow-hidden rounded-3xl border border-border bg-surface">
          {admins.map((a) => (
            <li key={a.id} className="flex items-center gap-3 px-5 py-3.5">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-accent-soft text-accent">
                <ShieldCheck size={16} strokeWidth={2} aria-hidden />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium tracking-tight text-text">
                  {a.name || a.email}
                  <span className="ml-2 rounded-full bg-accent-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-accent">
                    {a.role ? (ROLE_LABEL[a.role as AdminRole] ?? a.role) : "—"}
                  </span>
                </p>
                <p className="truncate text-xs text-text-tertiary">{a.email}</p>
              </div>
              {!a.is_super_admin && (
                <>
                  <select
                    value={a.role as "admin" | "manager"}
                    onChange={(e) => changeRole(a, e.target.value as "admin" | "manager")}
                    aria-label={`Perfil de ${a.email}`}
                    className="rounded-full border border-border bg-surface px-3 py-1.5 text-xs text-text-secondary outline-none focus:border-accent"
                  >
                    {ASSIGNABLE.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => revoke(a)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium text-text-secondary transition-colors hover:border-danger/40 hover:text-danger"
                  >
                    <Trash2 size={13} strokeWidth={2} aria-hidden />
                    Remover
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </AdminPage>
  );
}
